import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Shield, Database, Lock, Eye, Trash2, Mail, 
  Server, Smartphone, Cookie, Globe, AlertTriangle
} from "lucide-react";
import Layout from "@/components/Layout";

const PrivacyPolicy = () => {
  const lastUpdated = "April 13, 2026";

  const sections = [
    {
      id: "overview",
      icon: Shield,
      title: "Overview",
      color: "text-blue-400",
      content: `Pivot Performance is owned and operated by Pivot Point Athletics, LLC, a California limited liability company ("we," "our," or "us"). This Privacy Policy explains how we collect, use, and safeguard your information when you use our basketball conditioning application available at pivotperformances.com.`
    },
    {
      id: "data-collected",
      icon: Database,
      title: "Information We Collect",
      color: "text-green-400",
      content: null,
      subsections: [
        {
          title: "Account Information",
          items: [
            "Email address (required for registration)",
            "Name (optional display name)",
            "Password (encrypted and hashed, never stored in plain text)"
          ]
        },
        {
          title: "Workout & Progress Data",
          items: [
            "Workout completion history",
            "Vertical jump measurements",
            "Training session progress",
            "Badge achievements and points",
            "Program milestone completions"
          ]
        },
        {
          title: "Optional Features",
          items: [
            "Progress photos (only if you choose to upload)",
            "Video uploads for AI analysis (temporarily processed, then deleted)",
            "Team and tournament participation data"
          ]
        }
      ]
    },
    {
      id: "local-storage",
      icon: Smartphone,
      title: "Device-Only Storage (Not Sent to Servers)",
      color: "text-purple-400",
      content: "The following data is stored only on your device and is never transmitted to our servers:",
      items: [
        "Hydration tracking data (water intake)",
        "Voice coach preferences (style, speed, pitch)",
        "Rest break settings",
        "Daily challenge progress",
        "UI preferences and settings"
      ]
    },
    {
      id: "not-collected",
      icon: Eye,
      title: "Information We Do NOT Collect",
      color: "text-cyan-400",
      content: "We respect your privacy and explicitly do not collect:",
      items: [
        "Location or GPS data",
        "Device identifiers or advertising IDs",
        "Contact lists or phone numbers",
        "Browsing history outside our app",
        "Third-party tracking or analytics data",
        "Biometric data (fingerprints, face ID)",
        "Financial information (payments handled securely by Stripe)"
      ]
    },
    {
      id: "ai-features",
      icon: Server,
      title: "AI-Powered Features",
      color: "text-orange-400",
      content: null,
      subsections: [
        {
          title: "AI Drill Analyzer",
          description: "When you upload a video for movement analysis:",
          items: [
            "Videos are temporarily stored only during processing",
            "Videos are automatically deleted immediately after analysis",
            "Analysis results are not permanently stored",
            "We do not use your videos to train AI models"
          ]
        },
        {
          title: "AI Coaching",
          description: "Chat conversations with the AI coach:",
          items: [
            "Conversations are processed in real-time",
            "No conversation history is permanently stored on our servers",
            "AI responses are generated using secure, privacy-focused APIs"
          ]
        }
      ]
    },
    {
      id: "data-security",
      icon: Lock,
      title: "Data Security",
      color: "text-red-400",
      content: "We implement industry-standard security measures:",
      items: [
        "All data transmitted using HTTPS/TLS encryption",
        "Passwords hashed using secure algorithms (never stored in plain text)",
        "JWT tokens for secure authentication",
        "Regular security audits and updates",
        "Database access restricted and monitored"
      ]
    },
    {
      id: "data-sharing",
      icon: Globe,
      title: "Data Sharing",
      color: "text-yellow-400",
      content: "We do NOT sell your personal information. We may share data only in these limited circumstances:",
      items: [
        "With your explicit consent (e.g., team features, leaderboards)",
        "To comply with legal obligations",
        "With service providers who assist our operations (under strict confidentiality)",
        "Aggregated, anonymized statistics (never individual data)"
      ]
    },
    {
      id: "your-rights",
      icon: Shield,
      title: "Your Rights",
      color: "text-emerald-400",
      content: "You have the right to:",
      items: [
        "Access your personal data at any time",
        "Update or correct your information",
        "Delete your account and all associated data",
        "Export your workout history and progress",
        "Opt out of optional features like leaderboards"
      ]
    },
    {
      id: "data-retention",
      icon: Trash2,
      title: "Data Retention",
      color: "text-pink-400",
      content: null,
      subsections: [
        {
          title: "Active Accounts",
          items: [
            "Account data retained while your account is active",
            "Workout history preserved to track your progress"
          ]
        },
        {
          title: "Account Deletion",
          items: [
            "All personal data permanently deleted within 30 days of account deletion request",
            "Anonymized aggregate statistics may be retained"
          ]
        },
        {
          title: "Temporary Data",
          items: [
            "Uploaded videos: Deleted immediately after AI processing",
            "Session tokens: Expire after 24 hours"
          ]
        }
      ]
    },
    {
      id: "cookies",
      icon: Cookie,
      title: "Cookies & Local Storage",
      color: "text-amber-400",
      content: "We use minimal browser storage:",
      items: [
        "Authentication tokens (required for login)",
        "User preferences (stored locally, not on servers)",
        "No third-party tracking cookies",
        "No advertising or analytics cookies"
      ]
    },
    {
      id: "children",
      icon: AlertTriangle,
      title: "Children's Privacy",
      color: "text-rose-400",
      content: "Our service is intended for users 13 years of age and older. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us immediately."
    },
    {
      id: "contact",
      icon: Mail,
      title: "Contact Us",
      color: "text-indigo-400",
      content: "If you have questions about this Privacy Policy or your data, please contact us at:",
      contactInfo: {
        company: "Pivot Point Athletics, LLC",
        address: "1624 E. Stafford St, Santa Ana, CA 92701",
        email: "pivotpointathletics@gmail.com",
        subject: "Privacy Inquiry"
      }
    }
  ];

  return (
    <Layout>
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-white mb-2">Privacy Policy</h1>
            <p className="text-muted-foreground">
              Your privacy matters to us. Here's how we handle your data.
            </p>
            <Badge className="mt-3 bg-zinc-800 text-zinc-400">
              Last Updated: {lastUpdated}
            </Badge>
          </motion.div>

          {/* Quick Summary Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="mb-8 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-green-400 mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Quick Summary
                </h2>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span className="text-white">We collect only what's needed for the app to work</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span className="text-white">We never sell your personal data</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span className="text-white">No third-party tracking or advertising</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span className="text-white">Uploaded videos are deleted immediately after analysis</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    <span className="text-white">You can delete your account and all data anytime</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </motion.div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                >
                  <Card className="bg-zinc-900/80 border-zinc-800">
                    <CardHeader className="pb-2">
                      <CardTitle className={`text-lg font-bold flex items-center gap-2 ${section.color}`}>
                        <Icon className="w-5 h-5" />
                        {section.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-2">
                      {section.content && (
                        <p className="text-muted-foreground text-sm mb-3">{section.content}</p>
                      )}
                      
                      {section.items && (
                        <ul className="space-y-1.5">
                          {section.items.map((item, i) => (
                            <li key={i} className="text-sm text-white flex items-start gap-2">
                              <span className={section.color}>•</span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}

                      {section.subsections && (
                        <div className="space-y-4">
                          {section.subsections.map((sub, i) => (
                            <div key={i} className="pl-4 border-l-2 border-zinc-700">
                              <h4 className="font-medium text-white mb-1">{sub.title}</h4>
                              {sub.description && (
                                <p className="text-xs text-muted-foreground mb-2">{sub.description}</p>
                              )}
                              <ul className="space-y-1">
                                {sub.items.map((item, j) => (
                                  <li key={j} className="text-sm text-muted-foreground flex items-start gap-2">
                                    <span className={section.color}>→</span>
                                    {item}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}

                      {section.contactInfo && (
                        <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg space-y-1">
                          {section.contactInfo.company && (
                            <p className="text-white font-bold">{section.contactInfo.company}</p>
                          )}
                          {section.contactInfo.address && (
                            <p className="text-xs text-muted-foreground">{section.contactInfo.address}</p>
                          )}
                          <p className="text-white font-medium pt-1">{section.contactInfo.email}</p>
                          <p className="text-xs text-muted-foreground">Subject: {section.contactInfo.subject}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 text-center text-sm text-muted-foreground"
          >
            <p>
              By using Pivot Performance, you agree to this Privacy Policy.
            </p>
            <p className="mt-2">
              We may update this policy from time to time. We'll notify you of any significant changes.
            </p>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
